import {
  compact,
  filter,
  flatMap,
  get,
  head,
  includes,
  isEmpty,
  isNull,
  isNaN,
  map,
  omit,
  pickBy,
  some,
  toNumber,
  uniq,
  sortBy,
  size,
  gt,
  find,
  isFunction,
  has,
  isNil,
} from 'lodash';

import {
  IXVIListColumn,
  ColumnFilterParams,
  SortModelField,
  ColumnFilterModel,
  IFilterQuery,
  IQueryModificatorsData,
  FilterModel,
  ENTITY_TYPES,
  IComposeQueryOptions,
  IOrderByUseLabelPath,
} from '@shared/types';
import {
  prepareOdataString,
  isObjectIdValid,
  toMoment,
  getColumnIdWithLabelPath,
} from '@shared/utils';
import {
  ROW_SELECTOR_ID,
  DEFAULT_PAGE_SIZE,
  ROW_DRAG_COLUMN_ID,
  COLUMN_SELECTOR_ID,
} from '@config/index';
import { IUIListState } from '@store/reducers/ui';

export const composeOrderByFromModel = (
  sortModel: SortModelField[],
  orderByUseLabelPaths?: IOrderByUseLabelPath[],
): string => {
  if (!isEmpty(sortModel) && sortModel.length > 1) {
    // eslint-disable-next-line no-shadow
    return map(
      sortModel,
      ({ colId, sort }) =>
        `${getColumnIdWithLabelPath(colId, orderByUseLabelPaths)} ${sort}`,
    ).join(',');
  }

  let newColId = get(head(sortModel), 'colId');
  if (isEmpty(newColId)) {
    return '';
  }
  const newSort = get(head(sortModel), 'sort', 'asc');

  newColId = getColumnIdWithLabelPath(newColId, orderByUseLabelPaths);

  return `${newColId} ${newSort}`;
};

export const composeQuickFilterPart = (
  columns: IXVIListColumn[],
  filterText: string,
): string => {
  const applicableColumns = filter(columns, 'filterParams.quickFilter');
  if (isEmpty(applicableColumns) || isEmpty(filterText)) {
    return '';
  }

  const columnIds = map(applicableColumns, (column) =>
    get(column, 'labelPath', get(column, 'path')),
  );

  return map(
    columnIds,
    (field: string) =>
      `contains(${field.split('.').join('/')},'${prepareOdataString(filterText)}')`,
  ).join(' or ');
};

export const getISODate = (date: any, isEndTime: boolean = false): string => {
  if (isEndTime) {
    return toMoment(date).endOf('second').toISOString();
  }

  return toMoment(date).toISOString();
};

export const endOfDay = (input: any): string => {
  const date = toMoment(input).endOf('day');
  // date.setHours(23, 59, 59, 999);

  return date.toISOString();
};

// eslint-disable-next-line complexity
export const composeColumnFilter = (
  [field, filterModel]: [string, ColumnFilterModel],
  options?: {
    columnDefinition: IXVIListColumn[];
  },
): string => {
  const filterValue: any = get(filterModel, 'filter');
  const filterType = get(filterModel, 'type');
  const fieldName = field.split('.').join('/');
  const dateFrom = get(filterModel, 'dateFrom', '');
  const dateTo = get(filterModel, 'dateTo', '');
  // delete the priceFrom and priceTo which is unused code
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const filter = get(filterModel, 'filter');
  const filterTo = get(filterModel, 'filterTo');

  const { columnDefinition } = options ?? {};

  const column = find(columnDefinition, { path: field });

  const columnFilterStringBuilder = get(column, 'filterStringBuilder');

  // just return column filter string if it is defined
  if (isFunction(columnFilterStringBuilder)) {
    return (
      columnFilterStringBuilder({
        field,
        filterModel,
        filterType,
        filterValue,
        fieldName,
        column,
      }) ?? ''
    );
  }

  switch (filterType) {
    // delete Price which is unused
    case 'greaterThanOrEqual':
      if (!isNull(filter)) {
        return `${fieldName} ge ${filter}`;
      }
      break;

    case 'lessThanOrEqual':
      if (!isNull(filter)) {
        return `${fieldName} le ${filter}`;
      }
      break;

    // Number filter case
    case 'inRange':
      if (isNull(filter) && !isNull(filterTo)) {
        return `${fieldName} le ${filterTo}`;
      }
      if (!isNull(filter) && isNull(filterTo)) {
        return `${fieldName} ge ${filter}`;
      }

      if (!isNull(filter) && !isNull(filterTo)) {
        return `${fieldName} ge ${filter} and ${fieldName} le ${filterTo}`;
      }
      break;

    case 'equals':
      // Date filter case
      if (!isEmpty(dateFrom)) {
        return `${fieldName} ge ${getISODate(dateFrom)} and ${fieldName} le ${endOfDay(dateFrom)}`;
      }
      break;

    case 'inRangeDate':
      // Date filter case
      if (isEmpty(dateFrom) && !isEmpty(dateTo)) {
        return `${fieldName} le ${endOfDay(dateTo)}`;
      }
      if (!isEmpty(dateFrom) && isEmpty(dateTo)) {
        return `${fieldName} ge ${getISODate(dateFrom)}`;
      }

      if (!isEmpty(dateFrom) && !isEmpty(dateTo)) {
        const [startDay, endDay] = sortBy([
          toMoment(dateFrom).valueOf(),
          toMoment(dateTo).valueOf(),
        ]);
        const endDayAndTime = gt(size(dateTo), 10)
          ? getISODate(endDay, true)
          : endOfDay(endDay);

        return `${fieldName} ge ${getISODate(startDay)} and ${fieldName} le ${endDayAndTime}`;
      }

      break;

    case 'containsAny':
    case 'isEqual':
      if (isEmpty(filterValue)) return '';

      return `${fieldName} eq in(${filterValue.join(',')})`;

    case 'directlyEqual':
      if (isEmpty(filterValue)) return '';

      return `${fieldName} eq ${handleFilterValueByType(filterValue)}`;

    case 'containsAnyString':
      if (isEmpty(filterValue)) return '';

      return `${fieldName} eq instring(${filterValue.join(',')})`;

    case 'notContains':
      if (isEmpty(filterValue)) return '';

      return `${fieldName} eq nin(${filterValue.join(',')})`;

    case 'containsAll':
      return filterValue
        .map((item) => `${fieldName} eq objectid(${item})`)
        .join(' and ');

    case 'contains':
      if (!filterValue) return '';
      if (filterValue.split(',').every((val) => isObjectIdValid(val))) {
        return `${fieldName} eq in(${filterValue})`;
      }

      return `contains(${fieldName},'${prepareOdataString(filterValue)}')`;

    default:
      break;
  }

  return '';
};

const composeFilterByModel = (
  model: object,
  columnConfig: IXVIListColumn[],
  visibleColumnIds: string[],
): string[] => {
  const obligatoryColumns = filter(columnConfig, 'filterParams.includeAlways');
  const completeFilter = Object.assign(
    {},
    ...obligatoryColumns.map(
      ({
        path,
        filterParams,
      }: {
        path: string;
        filterParams: ColumnFilterParams;
      }) => ({
        [path]: get(filterParams, 'default'),
      }),
    ),
    pickBy(model, (filterValue: any, colId: string) =>
      includes(visibleColumnIds, colId),
    ),
  );

  return Object.entries(completeFilter).map((filterData) =>
    composeColumnFilter(filterData, { columnDefinition: columnConfig }),
  );
};

const applyFilterParam = (
  query: IFilterQuery,
  queryModificatorsData: IQueryModificatorsData,
): IFilterQuery => {
  const {
    listView,
    columnConfig,
    visibleColumnsConfig,
    visibleColumnIds,
    inShowDeletedMode,
  } = queryModificatorsData;
  const { filterModel, quickFilter } = listView;
  const newFilterModel = omit(filterModel, ROW_SELECTOR_ID, ROW_DRAG_COLUMN_ID);
  const filterParams = composeFilterByModel(
    newFilterModel,
    columnConfig,
    visibleColumnIds,
  ).concat([query.filter]);
  if (!query.notNeedActive) {
    const isActive = inShowDeletedMode ? 'false' : 'true';
    filterParams.push(`is_active eq in(${isActive})`);
  }
  const quickFilterParam = composeQuickFilterPart(
    visibleColumnsConfig,
    quickFilter,
  );
  filterParams.push(quickFilterParam);
  const filterString = map(
    compact(filterParams),
    (param: string) => `(${param})`,
  ).join(' and ');

  if (!isEmpty(filterString)) {
    query.filter = filterString;
  }

  return query;
};

const applySelectParam = (
  query: IFilterQuery,
  queryModificatorsData: IQueryModificatorsData,
): IFilterQuery => {
  // projection is composed from and selected columns paths and appended by
  // additional field that are required by this column which are defined by
  // select or labelPath property

  const discardParamsColumnList = [COLUMN_SELECTOR_ID];

  query.select = uniq([
    ...(query.select || []),
    ...compact(
      flatMap(
        queryModificatorsData.visibleColumnsConfig,
        (item: IXVIListColumn) => {
          if (
            !get(
              item,
              'discardParamsToBackend',
              includes(discardParamsColumnList, get(item, 'path', '')),
            )
          ) {
            return get(item, 'select', [
              get(item, 'labelPath', ''),
              get(item, 'path', ''),
            ]);
          }
        },
      ),
    ),
  ]);

  return query;
};

const applyPaginationParams = (
  query: IFilterQuery,
  queryModificatorsData: IQueryModificatorsData,
): IFilterQuery => {
  const { currentPage = 1, pageSize = DEFAULT_PAGE_SIZE } =
    queryModificatorsData.listView;

  const top = get(query, 'top');

  const pageSizeWithDefault = has(query, 'top') && !isNil(top) ? top : pageSize;

  query.top = pageSizeWithDefault;
  query.skip = pageSizeWithDefault * (currentPage - 1);

  return query;
};

const applyIncludeEntitiesParam = (
  query: IFilterQuery,
  queryModificatorsData: IQueryModificatorsData,
): IFilterQuery => {
  // Query is based on visible columns configuration, skip default include_entities value
  delete query.include_entities;

  const includeEntities = compact(
    uniq(
      map(queryModificatorsData.visibleColumnsConfig, 'filterParams.entity'),
    ),
  );

  if (!isEmpty(includeEntities)) {
    query.include_entities = includeEntities as ENTITY_TYPES[];
  }

  return query;
};

const applyOrderByParam = (
  query: IFilterQuery,
  queryModificatorsData: IQueryModificatorsData,
  orderByUseLabelPaths?: IOrderByUseLabelPath[],
): IFilterQuery => {
  const { listView } = queryModificatorsData;
  query.orderby = composeOrderByFromModel(
    listView.sortModel,
    orderByUseLabelPaths,
  );

  return query;
};

export const isAlwaysVisiblePredicate = (
  listColumn: IXVIListColumn,
): boolean => {
  return listColumn.defaultView || listColumn.hide === false;
};

const getVisibleColumnIds = (
  listView: IUIListState,
  columnConfig: IXVIListColumn[],
): string[] => {
  return uniq([
    ...map(filter(columnConfig, isAlwaysVisiblePredicate), 'path'),
    ...get(listView, 'selectedColumns', []),
  ]);
};

const getVisibleColumnsConfig = (
  columnConfig: IXVIListColumn[],
  visibleColumnIds: string[],
): IXVIListColumn[] =>
  filter(columnConfig, ({ path }: { path: string }) =>
    includes(visibleColumnIds, path),
  );

const handleFilterValueByType = (filterValue: string): string => {
  const isOnlyNumbersInString = !isNaN(toNumber(filterValue));

  return isOnlyNumbersInString ? `${filterValue}` : `'${filterValue}'`;
};

export const hasColumnsQuickFilterOption = (
  viewConfig: IUIListState,
  columnConfig: IXVIListColumn[],
): boolean => {
  const visibleColumnIds = getVisibleColumnIds(viewConfig, columnConfig);
  const visibleColumnsConfig = getVisibleColumnsConfig(
    columnConfig,
    visibleColumnIds,
  );

  return some(visibleColumnsConfig, (column) => {
    return column.marryChildren && !isEmpty(column.children)
      ? some(column.children, { filterParams: { quickFilter: true } })
      : column.filterParams?.quickFilter;
  });
};

export const composeQuery = (
  defaultQuery: IFilterQuery,
  data: IQueryModificatorsData,
  options?: IComposeQueryOptions,
): IFilterQuery => {
  const { ignoreFilter = false, ignoreSort = false } = options || {};

  let query = defaultQuery;
  const { listView, columnConfig } = data;
  const visibleColumnIds = getVisibleColumnIds(listView, columnConfig);
  const visibleColumnsConfig = getVisibleColumnsConfig(
    columnConfig,
    visibleColumnIds,
  );
  const visibleColumnFilters: FilterModel = pickBy(
    listView.filterModel,
    (model, key) => visibleColumnIds.includes(key),
  );
  const queryModificatorsData: IQueryModificatorsData = {
    ...data,
    visibleColumnIds,
    visibleColumnsConfig,
    visibleColumnFilters,
  };

  if (!ignoreFilter) {
    query = applyFilterParam(query, queryModificatorsData);
  }
  if (!ignoreSort) {
    query = applyOrderByParam(
      query,
      queryModificatorsData,
      get(options, 'orderByUseLabelPaths', []),
    );
  }
  query = applySelectParam(query, queryModificatorsData);
  query = applyPaginationParams(query, queryModificatorsData);
  query = applyIncludeEntitiesParam(query, queryModificatorsData);

  return query;
};
